import type { Lambda, LambdaDefaults, PhxApiV2Config } from './types/phx-api-v2';

export const DEFAULT_ARCHITECTURE = 'arm64';
export const DEFAULT_RUNTIME = 'java21';
export const DEFAULT_MEMORY = 2048;
export const DEFAULT_TIMEOUT = 60;
export const DEFAULT_SNAPSTART_ENABLED = false;
export const DEFAULT_SNAP_ALIAS_ENABLED = false;

/**
 * HTML number input min / max / step for Lambda memory (MB).
 * Guides browser spinners; onChange handlers do not clamp typed values.
 */
export const LAMBDA_MEMORY_MB_INPUT_PROPS = {
  min: 128,
  max: 10240,
  step: 64,
} as const;

/**
 * HTML number input min / max for Lambda timeout (seconds).
 * Lambda max is 900s; guides browser spinners (onChange does not clamp).
 */
export const LAMBDA_TIMEOUT_SECONDS_INPUT_PROPS = {
  min: 1,
  max: 900,
} as const;

/**
 * Syncs lambda_layers array to boolean flags (for backward compatibility)
 */
function syncLambdaLayersToFlags(obj: Lambda | LambdaDefaults): void {
  const objWithLayers = obj as Record<string, unknown>;
  if (objWithLayers && typeof objWithLayers === 'object' && objWithLayers.lambda_layers) {
    const layers = Array.isArray(objWithLayers.lambda_layers) ? objWithLayers.lambda_layers : [];
    obj.param_and_secrets_ext_enabled = layers.includes('secrets');
    obj.datadog_lambda_apm_enabled = layers.includes('datadog');
    delete objWithLayers.lambda_layers;
  }
}

/**
 * Applies default values to a lambda object.
 * Uses group defaults when available, otherwise falls back to system constants.
 */
function applyLambdaDefaults(
  lambda: Lambda,
  isAuthorizer: boolean,
  groupDefaults?: LambdaDefaults | null,
): void {
  const d = groupDefaults;
  if (!lambda.architecture) {
    lambda.architecture = d?.architecture ?? DEFAULT_ARCHITECTURE;
  }
  if (!lambda.runtime) {
    lambda.runtime = d?.runtime ?? DEFAULT_RUNTIME;
  }
  if (lambda.memory_size === null || lambda.memory_size === undefined) {
    lambda.memory_size = d?.memory_size ?? DEFAULT_MEMORY;
  }
  if (lambda.timeout === null || lambda.timeout === undefined) {
    lambda.timeout = d?.timeout ?? DEFAULT_TIMEOUT;
  }
  if (lambda.snapstart === undefined) {
    lambda.snapstart = d?.snapstart ?? (isAuthorizer ? true : DEFAULT_SNAPSTART_ENABLED);
  }
  // SnapStart requires the alias, so force snap_alias on whenever snapstart is on.
  // When snapstart is off, preserve the value the module would deploy today (explicit ->
  // group default -> authorizer:true / endpoint:false). Never turn it off here: some
  // lambdas route through the alias (api.json ":SNAP" integration) without SnapStart, so
  // stripping snap_alias would destroy an alias API Gateway still points at.
  if (lambda.snapstart === true) {
    lambda.snap_alias = true;
  } else if (lambda.snap_alias === undefined) {
    lambda.snap_alias = d?.snap_alias ?? (isAuthorizer ? true : DEFAULT_SNAP_ALIAS_ENABLED);
  }
}

/**
 * Normalizes a lambda group (endpoint or authorizer).
 * Normalizes defaults first so they can be applied when filling lambdas.
 */
function normalizeLambdaGroup(
  config: PhxApiV2Config,
  groupName: 'endpoint_lambdas' | 'authorizer_lambdas',
  isAuthorizer: boolean = false,
): void {
  const group = config[groupName];
  if (!group) return;

  // Normalize defaults first so we can use them when filling lambdas
  if (group.defaults) {
    syncLambdaLayersToFlags(group.defaults);
    const defaults = group.defaults;
    if (defaults.memory_size === null || defaults.memory_size === undefined) {
      defaults.memory_size = DEFAULT_MEMORY;
    }
    // Set default snapstart to true for authorizer lambdas defaults
    if (isAuthorizer && defaults.snapstart === undefined) {
      defaults.snapstart = true;
    }
  }

  // Normalize individual lambdas, filling from group defaults when values are missing
  if (group.lambdas) {
    Object.keys(group.lambdas).forEach(lambdaName => {
      const lambda = group.lambdas![lambdaName];
      syncLambdaLayersToFlags(lambda);
      applyLambdaDefaults(lambda, isAuthorizer, group.defaults);
    });
  }
}

/**
 * Normalizes an API config object to ensure all lambdas have runtime, architecture, memory, timeout, and snapstart
 */
export const normalizeApiConfig = (config: PhxApiV2Config): PhxApiV2Config => {
  const normalized = structuredClone(config);

  normalizeLambdaGroup(normalized, 'endpoint_lambdas', false);
  normalizeLambdaGroup(normalized, 'authorizer_lambdas', true);

  return normalized;
};
